import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

const EMAIL = "contact@cosmovex.com";

export default function Contact() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#3D4B59]">
      <Helmet>
        <title>Contact Us - Cosmovex</title>
        <meta
          name="description"
          content="Get in touch with Cosmovex. Questions, feedback, support, partnership or press enquiries — reach our team and we'll get back to you."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cosmovexapps.com/contact" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-lg font-black text-[#221A06]">C</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#26313B]">Cosmovex</h1>
              <p className="text-xs text-[#8A93A2] font-mono">// Get in touch</p>
            </div>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/" className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all">Home</Link>
            <Link to="/about" className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all">About</Link>
            <Link to="/blogs" className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all">Blog</Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#26313B]">Contact Cosmovex</h1>
          <p className="text-xl text-[#A8720E] mb-10">We'd love to hear from you</p>

          <p className="text-[#3D4B59] leading-relaxed mb-8">
            Cosmovex is an independent software studio based in India, building privacy-first
            mobile apps and free web tools. Whether you've found a bug, have a feature idea,
            need help with one of our apps, or want to talk partnerships or press — we read every
            message and aim to reply within 2&ndash;3 business days.
          </p>

          {/* Primary contact card */}
          <div className="bg-white border border-[#E1DCCE] rounded-2xl p-8 mb-8 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
            <h2 className="text-2xl font-bold mb-2 text-[#26313B]">Email us</h2>
            <p className="text-[#5B6B7C] mb-5">
              The fastest way to reach us is by email. Tell us which app or tool you're writing
              about and include any screenshots if it's a support question.
            </p>
            <a
              href={`mailto:${EMAIL}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-semibold hover:bg-[#DEA83B] transition"
            >
              ✉️ {EMAIL}
            </a>
          </div>

          {/* What to reach out about */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold mb-4 text-[#26313B]">What can we help with?</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border border-[#E1DCCE] rounded-xl p-5 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                <h3 className="font-semibold text-[#26313B] mb-1">App support</h3>
                <p className="text-[#5B6B7C] text-sm">Trouble with one of our Android apps or web tools? We'll help you sort it out.</p>
              </div>
              <div className="bg-white border border-[#E1DCCE] rounded-xl p-5 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                <h3 className="font-semibold text-[#26313B] mb-1">Feedback &amp; ideas</h3>
                <p className="text-[#5B6B7C] text-sm">Feature requests and product feedback genuinely shape what we build next.</p>
              </div>
              <div className="bg-white border border-[#E1DCCE] rounded-xl p-5 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                <h3 className="font-semibold text-[#26313B] mb-1">Privacy &amp; data</h3>
                <p className="text-[#5B6B7C] text-sm">Questions about your data or a deletion request? See our <Link to="/privacy-policy" className="text-[#0F6BBF] hover:underline">Privacy Policy</Link> or email us.</p>
              </div>
              <div className="bg-white border border-[#E1DCCE] rounded-xl p-5 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                <h3 className="font-semibold text-[#26313B] mb-1">Partnerships &amp; press</h3>
                <p className="text-[#5B6B7C] text-sm">Collaborations, cross-promotion or media enquiries are always welcome.</p>
              </div>
            </div>
          </section>

          <section className="mb-4">
            <h2 className="text-2xl font-bold mb-3 text-[#26313B]">Company details</h2>
            <p className="text-[#5B6B7C]">
              Cosmovex &mdash; independent app studio<br />
              India<br />
              Email: <a href={`mailto:${EMAIL}`} className="text-[#0F6BBF] hover:underline">{EMAIL}</a>
            </p>
          </section>
        </motion.div>
      </div>

      {/* Footer with legal links */}
      <footer className="border-t border-[#E1DCCE] py-10 text-center text-[#8A93A2]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-4 text-sm">
            <Link to="/" className="hover:text-[#26313B] transition">Home</Link>
            <Link to="/blogs" className="hover:text-[#26313B] transition">Blog</Link>
            <Link to="/about" className="hover:text-[#26313B] transition">About</Link>
            <Link to="/contact" className="hover:text-[#26313B] transition">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-[#26313B] transition">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[#26313B] transition">Terms of Service</Link>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} Cosmovex. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
